import { useState, type FormEvent } from "react";
import type {
  Placement,
  PlacementAdapter,
  PlacementCreateCommand,
  PlacementPlatform,
  PlacementResource,
  PlacementSizing,
  PlacementTarget,
} from "../api/types";

const targetOptions: PlacementTarget[] = [
  { resource: "alot", platform: "web" },
  { resource: "alot", platform: "android" },
  { resource: "alot", platform: "ios" },
  { resource: "wiki", platform: "web" },
  { resource: "wiki", platform: "android" },
  { resource: "wiki", platform: "ios" },
];

type Draft = {
  placementKey: string;
  displayName: string;
  aspectRatio: string;
  sizingMode: PlacementSizing["mode"];
  sizingValue: string;
  adapterKey: PlacementAdapter["key"];
  contextPath: string;
  targets: PlacementTarget[];
};

function initialDraft(placement?: Placement): Draft {
  const sizing = placement?.layout.sizing;
  const value = sizing?.mode === "fill_width"
    ? sizing.maxWidth
    : sizing?.mode === "fixed_width"
      ? sizing.width
      : sizing?.mode === "fixed_height"
        ? sizing.height
        : undefined;
  return {
    placementKey: placement?.placementKey ?? "",
    displayName: placement?.displayName ?? "",
    aspectRatio: placement ? String(placement.layout.aspectRatio) : "1.777777",
    sizingMode: sizing?.mode ?? "fill_width",
    sizingValue: value === undefined ? "" : String(value),
    adapterKey: placement?.adapter.key ?? "default",
    contextPath: typeof placement?.adapter.config.context_path === "string"
      ? placement.adapter.config.context_path
      : "filters.category_ids",
    targets: placement?.targets ?? [],
  };
}

function sameTarget(left: PlacementTarget, right: PlacementTarget): boolean {
  return left.resource === right.resource && left.platform === right.platform;
}

function sizing(mode: PlacementSizing["mode"], rawValue: string): PlacementSizing {
  const parsed = rawValue.trim() === "" ? undefined : Number(rawValue);
  if (mode === "fill_width") return parsed === undefined ? { mode } : { mode, maxWidth: parsed };
  if (mode === "fixed_width") return { mode, width: parsed ?? 0 };
  return { mode, height: parsed ?? 0 };
}

function adapter(key: PlacementAdapter["key"], contextPath: string): PlacementAdapter {
  return {
    key,
    version: 1,
    config: key === "category_intersection" ? { context_path: contextPath.trim() } : {},
  };
}

export function PlacementForm({
  placement,
  busy,
  submitLabel,
  onSubmit,
}: {
  placement?: Placement;
  busy: boolean;
  submitLabel: string;
  onSubmit: (command: PlacementCreateCommand, baseEntityVersion?: number) => void;
}) {
  const [draft, setDraft] = useState(() => initialDraft(placement));
  // The entity version belongs to the draft snapshot, not to a later query
  // refresh. Submitting this captured value lets the server reject a stale
  // editor instead of silently overwriting another administrator's revision.
  const [baseEntityVersion] = useState(() => placement?.entityVersion);
  const ratio = Number(draft.aspectRatio);
  const validRatio = Number.isFinite(ratio) && ratio > 0 && ratio <= 1000;
  const needsSize = draft.sizingMode !== "fill_width";
  const size = Number(draft.sizingValue);
  const validSize = !needsSize || (Number.isFinite(size) && size > 0);
  const validContextPath = draft.adapterKey === "default" || /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)*$/.test(draft.contextPath.trim());
  const validKey = /^[a-z][a-z0-9]*(\.[a-z][a-z0-9]*)+$/.test(draft.placementKey);
  const valid = validKey && draft.displayName.trim().length > 0 && draft.targets.length > 0 && validRatio && validSize && validContextPath;

  function toggleTarget(resource: PlacementResource, platform: PlacementPlatform) {
    const target = { resource, platform };
    setDraft((current) => ({
      ...current,
      targets: current.targets.some((item) => sameTarget(item, target))
        ? current.targets.filter((item) => !sameTarget(item, target))
        : [...current.targets, target],
    }));
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!valid) return;
    onSubmit({
      placementKey: draft.placementKey,
      displayName: draft.displayName.trim(),
      targets: draft.targets,
      layout: { aspectRatio: ratio, sizing: sizing(draft.sizingMode, draft.sizingValue) },
      adapter: adapter(draft.adapterKey, draft.contextPath),
    }, baseEntityVersion);
  }

  return (
    <form className="placement-editor" onSubmit={submit}>
      <div className="panel placement-form">
        <div className="field">
          <label htmlFor="placement-key">Ключ плейсмента</label>
          <input
            id="placement-key"
            value={draft.placementKey}
            onChange={(event) => setDraft({ ...draft, placementKey: event.target.value })}
            placeholder="web.jobs.inline"
            disabled={Boolean(placement)}
            aria-invalid={!validKey}
          />
          <small className="muted">После создания ключ нельзя изменить.</small>
        </div>
        <div className="field">
          <label htmlFor="placement-name">Название</label>
          <input id="placement-name" value={draft.displayName} onChange={(event) => setDraft({ ...draft, displayName: event.target.value })} />
        </div>

        <fieldset>
          <legend>Ресурсы и платформы</legend>
          <div className="target-grid">
            {targetOptions.map((target) => {
              const checked = draft.targets.some((item) => sameTarget(item, target));
              return (
                <label key={`${target.resource}-${target.platform}`} className="check-card">
                  <input type="checkbox" checked={checked} onChange={() => toggleTarget(target.resource, target.platform)} />
                  <span>{target.resource} · {target.platform}</span>
                </label>
              );
            })}
          </div>
        </fieldset>

        <div className="form-grid">
          <div className="field">
            <label htmlFor="aspect-ratio">Соотношение сторон (ширина / высота)</label>
            <input id="aspect-ratio" type="number" min="0.000001" max="1000" step="any" value={draft.aspectRatio} onChange={(event) => setDraft({ ...draft, aspectRatio: event.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="sizing-mode">Режим размера</label>
            <select id="sizing-mode" value={draft.sizingMode} onChange={(event) => setDraft({ ...draft, sizingMode: event.target.value as PlacementSizing["mode"] })}>
              <option value="fill_width">По ширине контейнера</option>
              <option value="fixed_width">Фиксированная ширина</option>
              <option value="fixed_height">Фиксированная высота</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="sizing-value">{draft.sizingMode === "fill_width" ? "Максимальная ширина, px (необязательно)" : draft.sizingMode === "fixed_width" ? "Ширина, px" : "Высота, px"}</label>
            <input id="sizing-value" type="number" min="0.000001" step="any" value={draft.sizingValue} onChange={(event) => setDraft({ ...draft, sizingValue: event.target.value })} />
          </div>
        </div>

        <div className="form-grid">
          <div className="field">
            <label htmlFor="adapter-key">Адаптер</label>
            <select id="adapter-key" value={draft.adapterKey} onChange={(event) => setDraft({ ...draft, adapterKey: event.target.value as PlacementAdapter["key"] })}>
              <option value="default">Без фильтрации контекста</option>
              <option value="category_intersection">Пересечение категорий</option>
            </select>
          </div>
          {draft.adapterKey === "category_intersection" && (
            <div className="field">
              <label htmlFor="context-path">Путь к категориям в context</label>
              <input id="context-path" value={draft.contextPath} onChange={(event) => setDraft({ ...draft, contextPath: event.target.value })} />
            </div>
          )}
        </div>

        <button className="primary" type="submit" disabled={busy || !valid}>{busy ? "Сохраняю…" : submitLabel}</button>
      </div>

      <div className="panel preview-panel">
        <h3>Холст плейсмента</h3>
        <p className="muted">Этот формат будет унаследован блочным DivKit-конструктором.</p>
        <div className="placement-canvas-wrap">
          <div className="placement-canvas" style={{ aspectRatio: validRatio ? String(ratio) : "1" }}>
            <span>{validRatio ? `${ratio}:1` : "Некорректная геометрия"}</span>
          </div>
        </div>
        <dl className="summary-list">
          <div><dt>Ревизия</dt><dd>{placement?.currentRevision ?? "будет 1"}</dd></div>
          <div><dt>Цели</dt><dd>{draft.targets.length || "не выбраны"}</dd></div>
          <div><dt>Адаптер</dt><dd>{draft.adapterKey}@1</dd></div>
        </dl>
      </div>
    </form>
  );
}
